import * as React from 'react'
import { cn } from '../../../lib/cn'

export interface MfaMethodOption {
  /** Factor identifier — stored in `allowed_factors`. Matches factor_types[].slug. */
  slug: string
  /** Human-readable name displayed on the chip. */
  name: string
}

export interface MfaMethodPickerProps {
  /** All factor types available in the tenant catalog. */
  options: MfaMethodOption[]
  /** Selected factor slugs (subset of options[].slug). */
  value: string[]
  onChange: (next: string[]) => void
  /**
   * Highlight a specific factor chip when the publish-validator returns
   * FLOW_REF_UNKNOWN_FACTOR. Pass the offending factor slug.
   */
  errorFor?: string
  disabled?: boolean
  className?: string
}

/**
 * MfaMethodPicker — multi-select chip row for MFA factor types.
 *
 * Reads from the tenant `factor_types` catalog. Each chip toggles its
 * factor slug into/out of the `value` array.
 *
 * Accessibility contract: same chip-button pattern as IdentifierChannelPicker.
 */
export const MfaMethodPicker = React.forwardRef<HTMLDivElement, MfaMethodPickerProps>(
  ({ options, value, onChange, errorFor, disabled = false, className }, ref) => {
    if (options.length === 0) {
      return (
        <div
          ref={ref}
          className={cn(
            'rounded-md border border-ia-border bg-ia-elevated px-3 py-2 text-[11px] text-ia-text-muted',
            className,
          )}
          role="status"
          aria-live="polite"
        >
          No MFA factor types configured. Visit MFA Factors to add one.
        </div>
      )
    }

    const toggle = (slug: string) => {
      if (disabled) return
      const next = value.includes(slug) ? value.filter((v) => v !== slug) : [...value, slug]
      onChange(next)
    }

    return (
      <div ref={ref} className={cn('flex flex-wrap gap-1.5', className)}>
        {options.map((opt) => {
          const isSelected = value.includes(opt.slug)
          const hasError = opt.slug === errorFor
          const errorId = hasError ? `mmp-error-${opt.slug}` : undefined

          return (
            <React.Fragment key={opt.slug}>
              <button
                type="button"
                aria-pressed={isSelected}
                aria-invalid={hasError ? true : undefined}
                aria-describedby={errorId}
                disabled={disabled}
                onClick={() => toggle(opt.slug)}
                className={cn(
                  'rounded-pill border px-2.5 py-1 text-xs transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                  'focus-visible:ring-ia-border-focus',
                  disabled && 'cursor-not-allowed opacity-50',
                  hasError
                    ? 'border-ia-error bg-ia-error/10 text-ia-error'
                    : isSelected
                      ? 'border-brand-500/40 bg-brand-500/15 text-foreground hover:bg-brand-500/25'
                      : 'border-ia-border bg-transparent text-muted-foreground hover:border-ia-border-strong hover:text-foreground',
                )}
              >
                {opt.name}
              </button>
              {hasError && (
                <span id={errorId} className="sr-only">
                  Unknown factor &ldquo;{opt.slug}&rdquo; — not in the tenant factor types catalog.
                </span>
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  },
)

MfaMethodPicker.displayName = 'MfaMethodPicker'
